import * as THREE from "three";
import { CoinFountain, Shockwaves, floorSigil, glowSprite, lightPillar, mouthGlow } from "./chestFx";
import { stagePlan } from "../ui/revealPlan";

/**
 * The reward chest's show around the model (chestStage.ts owns the renderer, the camera and the clips): the drop and
 * landing, the charge building under it, the light leaking from the lid while it rattles, and the burst — pillar,
 * shockwaves, flare, the coin fountain, the mouth lit from inside. Driven by `update` every frame with the lid's angle.
 * Presentation only; the rarity escalation is revealPlan.stagePlan's table.
 */
export class ChestShow {
  private readonly plan: ReturnType<typeof stagePlan>;
  private readonly sigil: ReturnType<typeof floorSigil>;
  private readonly pillar: ReturnType<typeof lightPillar>;
  private readonly waves: Shockwaves;
  private readonly seam: ReturnType<typeof glowSprite>;
  private readonly core: ReturnType<typeof glowSprite>;
  private readonly mouth: ReturnType<typeof mouthGlow>;
  private readonly fountain: CoinFountain;
  private readonly colour: THREE.Color;
  /** The chest's mouth (centre of the rim) and the front of the lid's seam, in stage world space. */
  readonly mouthAt = new THREE.Vector3();
  private readonly seamAt = new THREE.Vector3();
  private charge = 0;
  private burstT = -1;
  private dropT = -1;
  private landed = true;
  private t = 0;
  /** Called on the landing (the stage shakes its camera; the slot plays the thump). */
  onLand: (() => void) | null = null;
  /** Camera trauma the stage adds on top of the VFX library's shake (decays here). */
  kick = 0;

  constructor(private readonly scene: THREE.Scene, private readonly root: THREE.Object3D, private readonly rank: number, colour: readonly number[], private readonly scale: number) {
    const c = new THREE.Color(colour[0], colour[1], colour[2]);
    this.colour = c;
    this.plan = stagePlan(rank);
    this.sigil = floorSigil(c, this.plan.sigil);
    this.pillar = lightPillar(c, 0.15 * scale, this.plan.pillarHeight);
    this.waves = new Shockwaves(c, 5);
    this.seam = glowSprite(c, 3.2);
    this.core = glowSprite(c, 1);
    this.mouth = mouthGlow(c, 0.4 * scale, 0.24 * scale);
    this.fountain = new CoinFountain(c, this.plan.coins, this.plan.gems, scale);
    this.pillar.group.visible = false;
    scene.add(this.sigil.mesh, this.pillar.group, this.waves.group, this.seam.mesh, this.core.mesh, this.mouth.mesh, this.mouth.light, this.fountain.group);
    this.locate();
  }

  /** Finds the mouth and the seam from the lid's hinge (the lid node's origin): the mouth is half the depth in front of it. */
  private locate(): void {
    const lid = findPart(this.root, "lid");
    this.root.updateMatrixWorld(true);
    const hinge = new THREE.Vector3();
    if (lid) { lid.getWorldPosition(hinge); this.root.worldToLocal(hinge); } else hinge.set(0, 0.2, 0.15);
    const local = new THREE.Vector3(0, hinge.y, hinge.z - ChestShow.Depth / 2);
    this.mouthAt.copy(this.root.localToWorld(local.clone()));
    this.seamAt.copy(this.root.localToWorld(new THREE.Vector3(0, hinge.y + 0.01, hinge.z - ChestShow.Depth - 0.02)));
    this.mouth.mesh.position.copy(this.mouthAt).add(new THREE.Vector3(0, -0.012 * this.scale, 0));
    this.mouth.mesh.rotation.set(-Math.PI / 2, 0, this.root.rotation.y);
    this.mouth.light.position.copy(this.mouthAt).add(new THREE.Vector3(0, 0.12 * this.scale, 0.06 * this.scale));
    this.pillar.group.position.copy(this.mouthAt);
  }

  /** The chest's depth front to back in model units (art/hq_chest_kit.py D). */
  static readonly Depth = 0.3;

  /** The drop: the chest falls from above and lands with a slam (the rest is `update`). */
  drop(): void {
    this.dropT = 0;
    this.landed = false;
    this.root.position.y = this.plan.dropFrom;
  }

  /**
   * The rarity climb hands over from the tier before (chestStage.ts `upgrade`): the charge, the sigil's light and the
   * landing carry on, so only the colour changes; then the new chest pops in out of a flash with a ring in its colour.
   */
  adopt(prev: ChestShow): void {
    this.charge = prev.charge;
    this.sigil.level.value = prev.sigil.level.value;
    this.t = prev.t;
    this.popT = 0;
    this.flashT = 0;
    this.waves.spawn(this.plan.sigil * 2.4, 0.5, 0.5);
    this.waves.spawn(this.plan.sigil * 3.6, 0.75, 0.1);
    this.kick = Math.max(this.kick, 0.7);
  }
  /** Seconds since the tier-up pop (−1: none running) and since its flash. */
  private popT = -1;
  private flashT = -1;

  /** A landing where the chest already is (the flight from the end card): the slam's spring, a ring, the trauma. */
  land(): void {
    this.dropT = this.plan.dropS;
    this.landed = false;
  }

  /** A legendary's rain of gold over `centre` from `height` up, a moment after its burst (its own fountain). */
  rain(centre: THREE.Vector3, spread: number, height: number): void {
    const f = new CoinFountain(this.colour, 36, 0, this.scale);
    this.rainFountain = f;
    this.scene.add(f.group);
    window.setTimeout(() => f.rain(centre, spread, height), 450);
  }
  private rainFountain: CoinFountain | null = null;

  /** One rattle: the charge so far 0..1 (the sigil and the light inside climb with it). */
  rattle(charge: number): void {
    this.charge = charge;
    this.kick = Math.max(this.kick, 0.25 + 0.2 * charge);
  }

  /** The burst (the lock gone, the lid thrown): pillar, flare, shockwaves, the fountain. */
  burst(): void {
    this.burstT = 0;
    this.charge = 1;
    this.pillar.group.visible = true;
    for (let i = 0; i < this.plan.waves; i++) window.setTimeout(() => this.waves.spawn(this.plan.sigil * (3.0 + i * 1.2), 0.7 + i * 0.12, i === 0 ? 0.25 : 0.05), i * 110);
    this.fountain.burst(this.mouthAt.clone().add(new THREE.Vector3(0, 0.05, 0)), this.plan.power * Math.sqrt(this.scale / 2.1));
    this.kick = 1;
  }

  /** Per frame: `lidAngle` is how far the lid stands open (rad), `camera` faces the sprites. */
  update(dt: number, lidAngle: number, camera: THREE.Camera): void {
    this.t += dt;
    this.kick = Math.max(0, this.kick - dt * 2.2);
    this.dropUpdate(dt);
    this.waves.update(dt);
    this.fountain.update(dt);
    this.rainFountain?.update(dt);
    const opened = this.burstT >= 0;
    if (opened) this.burstT += dt;
    // the sigil: faint while it waits, climbing with each rattle, flaring at the burst then steady
    const sigilWant = opened ? 0.45 + 0.5 * Math.exp(-this.burstT * 2.5) : 0.12 + 0.4 * this.charge + 0.04 * Math.sin(this.t * 3);
    this.sigil.level.value += (sigilWant - this.sigil.level.value) * Math.min(1, dt * 6);
    // light leaking where the lid lifts: the seam flare follows the lid's opening (and the charge) until it bursts
    const crack = Math.min(1, Math.max(0, lidAngle) / 0.22);
    const here = this.landed ? 1 : 0;                         // nothing glows at the mouth while the chest is still falling
    this.seam.level.value = opened ? Math.max(0, this.seam.level.value - dt * 4) : crack * (0.35 + 0.6 * this.charge) * here;
    this.seam.mesh.position.copy(this.seamAt);
    this.seam.mesh.quaternion.copy(camera.quaternion);
    this.seam.mesh.scale.set(1.5 * this.scale * 0.5, 0.5 * this.scale * 0.5, 1);
    // the mouth glow and its light: a whisper while closed (only a lifting lid shows it), full after the burst
    const mouthWant = opened ? 0.35 + 0.8 * Math.exp(-this.burstT * 3) : (0.2 + 0.6 * this.charge) * crack * here;
    this.mouth.level.value += (mouthWant - this.mouth.level.value) * Math.min(1, dt * 10);
    this.mouth.light.intensity = opened ? 0.9 + 2.4 * Math.exp(-this.burstT * 3) : 1.2 * crack * this.charge * here;
    // the pillar: a flare that overshoots, then a breathing column; the core flash at the mouth
    if (opened) {
      const b = this.burstT;
      const flare = b < 0.12 ? b / 0.12 : Math.exp(-(b - 0.12) * 2.2);
      this.pillar.level.value = this.plan.pillarRest + (1.1 - this.plan.pillarRest) * flare + 0.04 * Math.sin(this.t * 4);
      this.pillar.group.scale.set(1 + 0.6 * flare, Math.min(1, b / 0.18), 1 + 0.6 * flare);
      this.core.level.value = 0.8 * Math.exp(-b * 7);
      this.core.mesh.scale.setScalar(this.scale * (0.8 + 1.6 * (1 - Math.exp(-b * 6))));
    } else if (this.flashT >= 0) {
      // a tier-up: a hot flash in the new colour that the new chest pops out of
      this.flashT += dt;
      this.core.level.value = 1.4 * Math.exp(-this.flashT * 7);
      this.core.mesh.scale.setScalar(this.scale * (1.2 + 2.2 * (1 - Math.exp(-this.flashT * 9))));
      if (this.flashT > 0.8) this.flashT = -1;
    } else this.core.level.value = 0;
    this.popUpdate(dt);
    this.core.mesh.position.copy(this.mouthAt).add(new THREE.Vector3(0, 0.1 * this.scale, 0));
    this.core.mesh.quaternion.copy(camera.quaternion);
  }

  /** The drop: ease-in fall, then a squash-and-stretch landing over ~0.45 s with a shockwave and dust. */
  private dropUpdate(dt: number): void {
    if (this.dropT < 0) return;
    this.dropT += dt;
    const fall = this.plan.dropS;
    if (this.dropT < fall) {
      const u = this.dropT / fall;
      this.root.position.y = this.plan.dropFrom * (1 - u * u);
      this.root.scale.set(this.baseScale.x * 0.94, this.baseScale.y * 1.08, this.baseScale.z * 0.94);
      return;
    }
    if (!this.landed) {
      this.landed = true;
      this.root.position.y = 0;
      this.waves.spawn(this.plan.sigil * 2.6, 0.55, 0.6);
      this.kick = 0.8;
      this.onLand?.();
    }
    const s = this.dropT - fall;
    const k = Math.exp(-s * 7) * Math.cos(s * 22);              // a damped spring: squash, overshoot, settle
    this.root.scale.set(this.baseScale.x * (1 + 0.14 * k), this.baseScale.y * (1 - 0.2 * k), this.baseScale.z * (1 + 0.14 * k));
    if (s > 0.6) { this.root.scale.copy(this.baseScale); this.dropT = -1; }
  }

  /** The tier-up pop: the new chest swells out of the flash and springs back to its size over ~0.5 s. */
  private popUpdate(dt: number): void {
    if (this.popT < 0 || this.dropT >= 0) return;
    this.popT += dt;
    const k = Math.exp(-this.popT * 6) * Math.cos(this.popT * 18);
    this.root.scale.copy(this.baseScale).multiplyScalar(1 + 0.22 * k);
    this.root.position.y = Math.max(0, 0.12 * this.scale * Math.exp(-this.popT * 9) * Math.sin(Math.min(Math.PI, this.popT * 14)));
    if (this.popT > 0.7) { this.root.scale.copy(this.baseScale); this.root.position.y = 0; this.popT = -1; }
  }
  get popping(): boolean { return this.popT >= 0; }

  /** The model's resting scale (set by the stage once; the drop and the rattle squash around it). */
  readonly baseScale = new THREE.Vector3(1, 1, 1);
  get dropping(): boolean { return this.dropT >= 0; }

  dispose(): void {
    this.fountain.dispose();
    if (this.rainFountain) { this.rainFountain.dispose(); this.scene.remove(this.rainFountain.group); }
    for (const o of [this.sigil.mesh, this.seam.mesh, this.core.mesh, this.mouth.mesh, ...this.pillar.group.children, ...this.waves.group.children] as THREE.Mesh[]) {
      o.geometry.dispose();
      (o.material as THREE.Material).dispose();
    }
    this.scene.remove(this.sigil.mesh, this.pillar.group, this.waves.group, this.seam.mesh, this.core.mesh, this.mouth.mesh, this.mouth.light, this.fountain.group);
  }
}

/** A chest part node by its part name (`chest_<rarity>.lid` / `chest_<rarity>lid` after the name fix). */
export function findPart(root: THREE.Object3D, part: string): THREE.Object3D | null {
  let hit: THREE.Object3D | null = null;
  root.traverse((o) => { if (!hit && (o.name.endsWith(`.${part}`) || new RegExp(`^chest_[a-z]+${part}$`).test(o.name))) hit = o; });
  return hit;
}

/** How far a hinged part stands open about its x axis (rad), from its node's rotation (q and −q are one rotation). */
export function hingeAngle(node: THREE.Object3D): number {
  const q = node.quaternion, sgn = q.w < 0 ? -1 : 1;
  return 2 * Math.atan2(sgn * q.x, sgn * q.w);
}
